import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Plus, LogIn, Copy, Check, ArrowLeft, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { TIMEZONES } from "@/lib/timezones";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const HERO = "https://images.unsplash.com/photo-1513267000941-598abe7be16f?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80";

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function TzSelect({ value, onChange, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="rounded-xl border-border bg-background/80 h-11">
        <SelectValue placeholder="Choose time zone" />
      </SelectTrigger>
      <SelectContent className="max-h-72">
        {TIMEZONES.map((t) => (
          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function Onboarding({ onConnected, onLogout }) {
  const [view, setView] = useState("landing"); // landing | create | join | code
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [created, setCreated] = useState(null);

  // create form
  const [cTitle, setCTitle] = useState("");
  const [cName, setCName] = useState("");
  const [cCity, setCCity] = useState("");
  const [cTz, setCTz] = useState("");
  const [cSince, setCSince] = useState("");
  const [cReunion, setCReunion] = useState("");

  // join form
  const [jCode, setJCode] = useState("");
  const [jName, setJName] = useState("");
  const [jCity, setJCity] = useState("");
  const [jTz, setJTz] = useState("");

  const submitCreate = async () => {
    if (!cName || !cCity || !cTz) return toast.error("Please fill your name, city and time zone.");
    setBusy(true);
    try {
      const { data } = await api.post("/couples", {
        couple_title: cTitle || null, partner_name: cName, partner_city: cCity,
        partner_timezone: cTz, together_since: cSince || null, reunion_date: cReunion || null,
      });
      setCreated(data);
      setView("code");
    } catch (e) {
      toast.error("Could not create your space. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const submitJoin = async () => {
    if (!jCode || !jName || !jCity || !jTz) return toast.error("Please fill every field.");
    setBusy(true);
    try {
      const { data } = await api.post("/couples/join", {
        join_code: jCode, partner_name: jName, partner_city: jCity, partner_timezone: jTz,
      });
      toast.success(`You're connected with ${data.partner_a?.name}! 💕`);
      onConnected(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not join. Check the code.");
    } finally {
      setBusy(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(created.join_code);
    setCopied(true);
    toast.success("Code copied — send it to your love ✨");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* atmospheric background */}
      <div className="absolute inset-0 -z-10">
        <img src={HERO} alt="" className="w-full h-72 object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/85 to-background" />
        <div className="absolute inset-0 grain opacity-[0.15]" />
      </div>

      <div className="max-w-md mx-auto px-5 pt-16 pb-10">
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-2"
        >
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-full bg-primary flex items-center justify-center shadow-lg pulse-glow">
              <Heart className="h-5 w-5 text-primary-foreground fill-primary-foreground" />
            </div>
            <span className="text-xs uppercase tracking-[0.25em] font-bold text-primary">Long Distance Us</span>
          </div>
          {onLogout && (
            <button data-testid="onboarding-logout-btn" onClick={onLogout} className="text-xs font-semibold text-muted-foreground hover:text-primary">
              Sign out
            </button>
          )}
        </motion.div>

        <AnimatePresence mode="wait">
          {view === "landing" && (
            <motion.div
              key="landing" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }} className="pt-6"
            >
              <h1 className="font-serif text-4xl sm:text-5xl font-semibold leading-tight tracking-tight text-foreground">
                Two hearts,<br />one little world.
              </h1>
              <p className="mt-4 text-sm sm:text-base text-muted-foreground leading-relaxed max-w-sm">
                Close the distance with shared countdowns, side-by-side clocks, daily questions,
                date ideas and a wall of your favorite memories.
              </p>

              <div className="mt-8 space-y-3">
                <Button
                  data-testid="start-create-btn" onClick={() => setView("create")}
                  className="w-full rounded-full h-12 text-base font-semibold shadow-md active:scale-95"
                >
                  <Plus className="h-5 w-5 mr-1" /> Create our space
                </Button>
                <Button
                  data-testid="start-join-btn" variant="outline" onClick={() => setView("join")}
                  className="w-full rounded-full h-12 text-base font-semibold border-border bg-card/70 active:scale-95"
                >
                  <LogIn className="h-5 w-5 mr-1" /> Join with a code
                </Button>
              </div>

              <div className="mt-10 grid grid-cols-3 gap-3 text-center">
                {[["🌍", "Time zones"], ["⏳", "Countdown"], ["📸", "Memories"]].map(([e, t]) => (
                  <div key={t} className="rounded-2xl bg-card/70 border border-border/60 py-4 backdrop-blur-sm">
                    <div className="text-2xl">{e}</div>
                    <div className="mt-1 text-[11px] font-medium text-muted-foreground">{t}</div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {view === "create" && (
            <motion.div key="create" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }}>
              <button onClick={() => setView("landing")} className="flex items-center gap-1 text-sm text-muted-foreground mb-4 mt-2">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              <h2 className="font-serif text-3xl font-semibold">Start your space</h2>
              <p className="text-sm text-muted-foreground mt-1 mb-6">Tell us about you — you'll get a code to invite your partner.</p>

              <div className="space-y-4 rounded-3xl bg-card border border-border/70 p-5 shadow-sm">
                <Field label="Couple name (optional)">
                  <Input data-testid="create-title-input" value={cTitle} onChange={(e) => setCTitle(e.target.value)} placeholder="e.g. Sam & Alex" className="rounded-xl h-11" />
                </Field>
                <Field label="Your name">
                  <Input data-testid="create-name-input" value={cName} onChange={(e) => setCName(e.target.value)} placeholder="Your name" className="rounded-xl h-11" />
                </Field>
                <Field label="Your city">
                  <Input data-testid="create-city-input" value={cCity} onChange={(e) => setCCity(e.target.value)} placeholder="e.g. Berlin" className="rounded-xl h-11" />
                </Field>
                <Field label="Your time zone">
                  <TzSelect value={cTz} onChange={setCTz} testid="create-tz-select" />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Together since">
                    <Input data-testid="create-since-input" type="date" value={cSince} onChange={(e) => setCSince(e.target.value)} className="rounded-xl h-11" />
                  </Field>
                  <Field label="Next reunion">
                    <Input data-testid="create-reunion-input" type="date" value={cReunion} onChange={(e) => setCReunion(e.target.value)} className="rounded-xl h-11" />
                  </Field>
                </div>
                <Button data-testid="create-submit-btn" disabled={busy} onClick={submitCreate} className="w-full rounded-full h-12 font-semibold mt-2">
                  {busy ? "Creating…" : "Create & get code"}
                </Button>
              </div>
            </motion.div>
          )}

          {view === "join" && (
            <motion.div key="join" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }}>
              <button onClick={() => setView("landing")} className="flex items-center gap-1 text-sm text-muted-foreground mb-4 mt-2">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              <h2 className="font-serif text-3xl font-semibold">Join your partner</h2>
              <p className="text-sm text-muted-foreground mt-1 mb-6">Enter the 6-character code they shared with you.</p>

              <div className="space-y-4 rounded-3xl bg-card border border-border/70 p-5 shadow-sm">
                <Field label="Join code">
                  <Input
                    data-testid="join-code-input" value={jCode}
                    onChange={(e) => setJCode(e.target.value.toUpperCase().slice(0, 6))}
                    placeholder="ABC123"
                    className="rounded-xl h-12 text-center font-mono text-2xl tracking-[0.4em] uppercase"
                  />
                </Field>
                <Field label="Your name">
                  <Input data-testid="join-name-input" value={jName} onChange={(e) => setJName(e.target.value)} placeholder="Your name" className="rounded-xl h-11" />
                </Field>
                <Field label="Your city">
                  <Input data-testid="join-city-input" value={jCity} onChange={(e) => setJCity(e.target.value)} placeholder="e.g. Tokyo" className="rounded-xl h-11" />
                </Field>
                <Field label="Your time zone">
                  <TzSelect value={jTz} onChange={setJTz} testid="join-tz-select" />
                </Field>
                <Button data-testid="join-submit-btn" disabled={busy} onClick={submitJoin} className="w-full rounded-full h-12 font-semibold mt-2">
                  {busy ? "Connecting…" : "Connect our hearts"}
                </Button>
              </div>
            </motion.div>
          )}

          {view === "code" && created && (
            <motion.div key="code" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="pt-6 text-center">
              <div className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] font-bold text-primary mb-3">
                <Sparkles className="h-4 w-4" /> Your space is ready
              </div>
              <h2 className="font-serif text-3xl font-semibold">Share your code</h2>
              <p className="text-sm text-muted-foreground mt-2 mb-6 max-w-xs mx-auto">
                Send this to your partner so they can join {created.couple_title ? `“${created.couple_title}”` : "your space"}.
              </p>

              <div className="rounded-3xl bg-card border border-border/70 p-6 shadow-sm">
                <div data-testid="generated-join-code" className="font-mono text-4xl sm:text-5xl font-bold tracking-[0.3em] text-primary">
                  {created.join_code}
                </div>
                <Button data-testid="copy-code-btn" variant="outline" onClick={copyCode} className="rounded-full mt-5 w-full h-11 border-border">
                  {copied ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                  {copied ? "Copied!" : "Copy code"}
                </Button>
              </div>

              <Button
                data-testid="enter-space-btn"
                onClick={() => onConnected(created)}
                className="w-full rounded-full h-12 font-semibold mt-5"
              >
                Enter our space
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
